/**
 * @Description This is FasatDatePicker component page.
 * @FileName fasatDatePicker.js
 * @Author SAMAPTI TALUKDAR-talusam
 * @CreatedOn 17 February, 2021 07:23:35
 * @IssueID CDTFASATMOD- 570
 */
import PropTypes from 'prop-types';
import { useCallback, forwardRef, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@manulife/mux';
import FasatLabel from '../fasatLabel/fasatLabel';
import './fasatDatePicker.scss';
import { globalConstants, staticCommonLabelKeys } from '../../moduleConstants';

// ============================================================================
// CONSTANTS
// ============================================================================

/** Date format constant from global config */
const { dateFormatMMDDYYYY } = globalConstants;

/** Number of parts in a complete date (month, day, year) */
const FULL_DATE_PARTS_COUNT = 3;

/** Index of year part in MM/DD/YYYY split array */
const YEAR_PART_INDEX = 2;

/** CSS class for error state */
const ERROR_CSS_CLASS = 'withError';

/** DOM selectors for MUX DatePicker segments */
const MUX_SEGMENT_SELECTORS = {
  DAY: '.mux-DatePicker-DateSegment[data-type="day"]',
  MONTH: '.mux-DatePicker-DateSegment[data-type="month"]',
  YEAR: '.mux-DatePicker-DateSegment[data-type="year"]',
};

/** Regex patterns for date validation */
const DATE_PATTERNS = {
  DIGITS_ONLY: /^\d+$/,
  FOUR_DIGIT_YEAR: /^\d{4}$/,
};

// ============================================================================
// HELPER FUNCTIONS - Error State
// ============================================================================

/**
 * Check if errors array indicates an error state
 * @param {Array} errors - Array of error objects
 * @returns {boolean} True if there's an error state
 */
const hasErrorState = (errors) => Array.isArray(errors) && errors.length > 0 && !!errors[0]?.isError;

/**
 * Get error CSS class if errors exist
 * @param {Array} errors - Array of error objects
 * @param {string} className - CSS class to return if error exists
 * @returns {string} CSS class or empty string
 */
const getErrorCssClass = (errors, className) => (errors.length > 0 && errors[0]?.isError ? className : '');

// ============================================================================
// HELPER FUNCTIONS - DOM Segment Querying
// ============================================================================

/**
 * Build date string from MUX DatePicker DOM segments.
 * This is used to detect partial date entry when MUX returns null.
 * @param {Object} containerRef - React ref to the container element
 * @param {string} dateFormat - Date format (MM/DD/YYYY or YYYY-MM-DD)
 * @returns {string|null} Partial or complete date string, or null if empty
 */
const getDateFromSegments = (containerRef, dateFormat) => {
  if (!containerRef?.current) {
    return null;
  }

  // Query DOM segments from MUX DatePicker internal structure
  const day = containerRef.current.querySelector(MUX_SEGMENT_SELECTORS.DAY)?.innerText;
  const month = containerRef.current.querySelector(MUX_SEGMENT_SELECTORS.MONTH)?.innerText;
  const year = containerRef.current.querySelector(MUX_SEGMENT_SELECTORS.YEAR)?.innerText;

  // Check if any segment has valid number (not placeholder text)
  const isValidSegment = (val) => val && DATE_PATTERNS.DIGITS_ONLY.test(val);
  const hasDay = isValidSegment(day);
  const hasMonth = isValidSegment(month);
  const hasYear = isValidSegment(year);

  // If no segments filled, return null (field is empty)
  if (!hasDay && !hasMonth && !hasYear) {
    return null;
  }

  // Build date string with whatever segments are available (partial or complete)
  const dayVal = hasDay ? day : '';
  const monthVal = hasMonth ? month : '';
  const yearVal = hasYear ? year : '';

  if (dateFormat === dateFormatMMDDYYYY) {
    return `${monthVal}/${dayVal}/${yearVal}`;
  }
  return `${yearVal}-${monthVal}-${dayVal}`;
};

// ============================================================================
// HELPER FUNCTIONS - Date Format Conversion
// ============================================================================

/**
 * Convert MM/DD/YYYY to ISO format (YYYY-MM-DD) for MUX.
 * Only converts complete dates; leaves partials intact.
 * @param {string} value - Date string in MM/DD/YYYY format
 * @param {string} dateFormat - Current date format
 * @returns {string} ISO format date or original value
 */
const convertToISOFormat = (value, dateFormat) => {
  if (dateFormat === dateFormatMMDDYYYY && typeof value === 'string' && value.includes('/')) {
    const parts = value.split('/');
    if (parts.length === FULL_DATE_PARTS_COUNT && parts[YEAR_PART_INDEX]) {
      const [month, day, year] = parts;
      return `${year}-${month}-${day}`;
    }
    return value;
  }
  return value;
};

/**
 * Convert ISO format from MUX back to MM/DD/YYYY.
 * Pass through user-typed values unchanged.
 * @param {string} value - Date string from MUX (ISO) or user input
 * @param {string} dateFormat - Target date format
 * @returns {string} Formatted date string
 */
const formatDateValue = (value, dateFormat) => {
  if (dateFormat === dateFormatMMDDYYYY && typeof value === 'string') {
    // If value comes back from MUX as ISO, convert to MM/DD/YYYY
    if (value.includes('-')) {
      const [year, month, day] = value.split('-');
      return `${month}/${day}/${year}`;
    }
    // If value includes '/', it is a user-typed MM/DD or MM/DD/YYYY - preserve as-is
    if (value.includes('/')) {
      return value;
    }
  }
  return value;
};

// ============================================================================
// HELPER FUNCTIONS - Date Validation
// ============================================================================

/**
 * Check if a date string is a valid complete date.
 * Validates format and requires 4-digit year to prevent MUX crashes.
 * @param {string} value - Date string to validate
 * @param {string} dateFormat - Expected date format
 * @returns {boolean} True if date is complete and valid
 */
const isValidCompleteDate = (value, dateFormat) => {
  if (!value || typeof value !== 'string') {
    return false;
  }

  const isDigits = (val) => val && DATE_PATTERNS.DIGITS_ONLY.test(val);
  const isFourDigitYear = (val) => val && DATE_PATTERNS.FOUR_DIGIT_YEAR.test(val);

  if (dateFormat === dateFormatMMDDYYYY) {
    // Check MM/DD/YYYY format - all parts must be present, numeric, and year must be 4 digits
    const mmddParts = value.split('/');
    if (mmddParts.length !== FULL_DATE_PARTS_COUNT) {
      return false;
    }
    const [mmddMonth, mmddDay, mmddYear] = mmddParts;
    return isDigits(mmddMonth) && isDigits(mmddDay) && isFourDigitYear(mmddYear);
  }

  // Check ISO format YYYY-MM-DD
  const isoParts = value.split('-');
  if (isoParts.length !== FULL_DATE_PARTS_COUNT) {
    return false;
  }
  const [isoYear, isoMonth, isoDay] = isoParts;
  return isFourDigitYear(isoYear) && isDigits(isoMonth) && isDigits(isoDay);
};

// ============================================================================
// HELPER FUNCTIONS - CSS & Locale
// ============================================================================

/**
 * Build CSS class name for the DatePicker wrapper
 * @param {boolean} hideDefaultErrorBorder - Hide default error border
 * @param {boolean} hideMuxLabel - Hide MUX internal label
 * @param {boolean} useExternalLabel - Using external FasatLabel
 * @returns {string} Combined CSS class names
 */
const buildFasatClassName = (hideDefaultErrorBorder, hideMuxLabel, useExternalLabel) => [
  'fasatDatePicker',
  hideDefaultErrorBorder && 'customDatePickerError',
  (hideMuxLabel || useExternalLabel) && 'hideMuxLabel',
].filter(Boolean).join(' ');

/**
 * Get locale based on date format (MM/DD/YYYY always uses en-US)
 * @param {string} dateFormat - Date format
 * @param {string} theLocale - Requested locale
 * @returns {string} Locale to use
 */
const getLocaleForFormat = (dateFormat, theLocale) => (
  dateFormat === dateFormatMMDDYYYY ? 'en-US' : theLocale
);

/**
 * Get additional text for external label (date format hint)
 * @param {string} dateFormat - Date format
 * @param {Function} tFn - Translation function
 * @returns {string} Additional text like "(MM/DD/YYYY)"
 */
const getExternalAdditionalText = (dateFormat, tFn) => (
  dateFormat === dateFormatMMDDYYYY ? tFn(staticCommonLabelKeys.COMMON_DATE_FORMAT_MM_DD_YYYY) : ''
);

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * FasatDatePicker - Wrapper component for MUX DatePicker with enhanced validation.
 *
 * Features:
 * - Detects partial date entry via DOM segment querying
 * - Validates 4-digit year requirement to prevent MUX crashes
 * - Converts between MM/DD/YYYY and ISO formats
 * - Supports external label with date format hint
 * - Proper focus/blur handling for validation
 *
 * @param {Object} props - Component props
 * @param {React.Ref} ref - Forwarded ref
 */
function FasatDatePickerComponent(props, ref) {
  const [t] = useTranslation();

  // Internal ref for querying DOM segments (partial date detection)
  const containerRef = useRef(null);

  // Destructure props - spread restProps to MUX to avoid conflicts
  const {
    dateValue,
    errors,
    hideDefaultErrorBorder,
    dateFormat,
    locale: localeProp,
    'data-testid': dataTestId,
    hideMuxLabel,
    onChange,
    onBlur,
    ariaLabel,
    'aria-label': ariaLabelProp,
    label,
    id,
    ...restProps
  } = props;

  const accessibilityLabel = ariaLabel || ariaLabelProp;
  const hasError = hasErrorState(errors);

  // -------------------------------------------------------------------------
  // Internal State - Only holds valid complete dates to prevent MUX crashes
  // -------------------------------------------------------------------------
  const [internalPickerValue, setInternalPickerValue] = useState(() => {
    const isoValue = convertToISOFormat(dateValue, dateFormat);
    return isValidCompleteDate(dateValue, dateFormat) ? isoValue : '';
  });

  // Sync internal state from props only when it's a valid complete date
  useEffect(() => {
    if (isValidCompleteDate(dateValue, dateFormat)) {
      setInternalPickerValue(convertToISOFormat(dateValue, dateFormat));
    } else if (!dateValue) {
      setInternalPickerValue('');
    } else {
      // For partial/invalid dates, don't update internal state - let MUX keep its current display
    }
  }, [dateValue, dateFormat]);

  // -------------------------------------------------------------------------
  // Derived Values
  // -------------------------------------------------------------------------
  const useExternalLabel = !!label;
  const externalAdditionalText = getExternalAdditionalText(dateFormat, t);
  const passLabelToMux = useExternalLabel ? '' : (label ?? accessibilityLabel);
  const className = buildFasatClassName(hideDefaultErrorBorder, hideMuxLabel, useExternalLabel);
  const locale = getLocaleForFormat(dateFormat, localeProp);

  // -------------------------------------------------------------------------
  // Event Handlers
  // -------------------------------------------------------------------------

  /**
   * Handle date value changes from MUX DatePicker
   */
  const handleChange = useCallback((value) => {
    // When MUX returns null/undefined, check if user has partially entered a date via DOM segments
    if (!value) {
      const segmentDate = getDateFromSegments(containerRef, dateFormat);
      if (segmentDate) {
        // Send the partial/incomplete date value to parent for validation
        setInternalPickerValue('');
        onChange(segmentDate);
        return;
      }
      // User cleared everything
      setInternalPickerValue('');
      onChange(null);
      return;
    }

    // Check if this is a valid complete date before updating internal state
    const formattedValue = formatDateValue(value, dateFormat);
    if (isValidCompleteDate(formattedValue, dateFormat)) {
      setInternalPickerValue(value); // Keep ISO format for MUX
    } else {
      setInternalPickerValue('');
    }
    onChange(formattedValue);
  }, [onChange, dateFormat]);

  /**
   * Handle blur event - validate and notify parent
   */
  const handleBlur = useCallback((event) => {
    const segmentDate = getDateFromSegments(containerRef, dateFormat);
    if (segmentDate) {
      onChange(segmentDate);
    } else {
      // All segments are cleared - notify parent with null for required validation
      onChange(null);
    }
    // Pass date value to onBlur so parent can validate immediately
    if (onBlur) {
      onBlur(event, segmentDate);
    }
  }, [onChange, onBlur, dateFormat]);

  /**
   * Handle focus event - placeholder for potential future use
   */
  const handleFocus = useCallback(() => {
    // Currently no-op, can be extended if focus tracking needed
  }, []);

  /**
   * Handle focus out - fires when focus leaves any element inside container
   */
  const handleFocusOut = useCallback(() => {
    requestAnimationFrame(() => {
      const isOutsideContainer = containerRef.current
        && !containerRef.current.contains(document.activeElement);
      if (isOutsideContainer) {
        const segmentDate = getDateFromSegments(containerRef, dateFormat);
        onChange(segmentDate);
      }
    });
  }, [onChange, dateFormat]);

  /**
   * Combine internal ref with forwarded ref
   */
  const setRefs = useCallback((node) => {
    containerRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    } else {
      // No ref provided, nothing to do
    }
  }, [ref]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div
      ref={setRefs}
      className={`${className} ${getErrorCssClass(errors, ERROR_CSS_CLASS)}`}
      onFocus={handleFocus}
      onBlur={handleFocusOut}
    >
      {/* External label with date format hint */}
      {useExternalLabel && (
        <FasatLabel
          className="fasat-external-label"
          htmlFor={`${id || 'datePicker'}-input`}
          additionalTag
          additionalText={externalAdditionalText}
        >
          {label}
        </FasatLabel>
      )}
      <DatePicker
        {...restProps}
        id={id}
        locale={locale}
        label={passLabelToMux}
        value={internalPickerValue}
        customStyle={{
          rootStyle: {
            width: '100%',
          },
        }}
        data-testid={dataTestId}
        onChange={handleChange}
        onBlur={handleBlur}
        aria-label={accessibilityLabel}
        aria-invalid={hasError}
      />
    </div>
  );
}

// ============================================================================
// PROP TYPES & DEFAULT PROPS
// ============================================================================

FasatDatePickerComponent.propTypes = {
  /** Date value in MM/DD/YYYY or ISO format */
  dateValue: PropTypes.string,
  /** Array of error objects for validation display */
  errors: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    msg: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    isError: PropTypes.bool,
  })),
  /** Unique identifier for the date picker */
  id: PropTypes.string,
  /** Hide default MUX error border styling */
  hideDefaultErrorBorder: PropTypes.bool,
  /** Date format (MM/DD/YYYY or YYYY-MM-DD) */
  dateFormat: PropTypes.string,
  /** Locale for date display */
  locale: PropTypes.string,
  /** Hide MUX internal label */
  hideMuxLabel: PropTypes.bool,
  /** Test ID for automated testing */
  dataTestId: PropTypes.string,
  /** External label text */
  label: PropTypes.string,
  /** Accessibility label */
  ariaLabel: PropTypes.string,
  /** Change handler - receives formatted date value */
  onChange: PropTypes.func,
  /** Blur handler - receives event and date value */
  onBlur: PropTypes.func,
};

FasatDatePickerComponent.defaultProps = {
  dateValue: null,
  errors: [],
  id: 'datePicker',
  hideDefaultErrorBorder: false,
  dateFormat: dateFormatMMDDYYYY,
  locale: 'en-US',
  hideMuxLabel: false,
  dataTestId: 'fasat-date-picker',
  label: null,
  ariaLabel: null,
  onChange: () => {},
  onBlur: () => {},
};

// ============================================================================
// EXPORT
// ============================================================================

const FasatDatePicker = forwardRef(FasatDatePickerComponent);

export default FasatDatePicker;
